import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { HouseFurniture } from '../../../models/apiTypes';
import useInteriorHistoryStore, { InteriorSnapshot } from '../../../stores/useInteriorHistoryStore';
import { toSnapshot, snapshotSignature, previewSnapshotId, isPreviewSnapshotId, roomPreviews } from './history';
import useFurniturePreviewStore, { type PreviewItem } from '../../../stores/useFurniturePreviewStore';
import { toPlacementMode } from '../interaction/placement';
import { stageLayout, type LayoutPatch } from './layoutSync';
import type { RoomSceneState } from './roomSceneState';

// ── Undo/redo history (session-scoped, in-memory) ──
// Extracted verbatim from RoomCanvas (decomposition step 06). Owns the
// auto-capture effect, reset-on-unmount, and the diff-replay of a target
// revision. `sceneRef.current.drag.captureSuspended` stays owned by RoomCanvas' drag code and is
// passed in as a parameter.

export interface UseRoomHistoryParams {
  mapObjectId: number;
  houseFurniture: HouseFurniture[];
  houseFurnitureLoaded: boolean;
  houseFurnitureFetching: boolean;
  canManageFurniture: boolean;
  sceneRef: React.MutableRefObject<RoomSceneState>;
  removeGreenOutline: () => void;
}

export interface UseRoomHistoryResult {
  canUndo: boolean;
  canRedo: boolean;
  handleUndo: () => void;
  handleRedo: () => void;
}

export function useRoomHistory({
  mapObjectId,
  houseFurniture,
  houseFurnitureLoaded,
  houseFurnitureFetching,
  canManageFurniture,
  sceneRef,
  removeGreenOutline,
}: UseRoomHistoryParams): UseRoomHistoryResult {
  const queryClient = useQueryClient();

  const historyInit = useInteriorHistoryStore((s) => s.init);
  const historyRecord = useInteriorHistoryStore((s) => s.record);
  const historyUndo = useInteriorHistoryStore((s) => s.undo);
  const historyRedo = useInteriorHistoryStore((s) => s.redo);
  const historyReset = useInteriorHistoryStore((s) => s.reset);
  const canUndo = useInteriorHistoryStore((s) => s.cursor > 0);
  const canRedo = useInteriorHistoryStore((s) => s.cursor < s.revisions.length - 1);
  // Signature of the snapshot we just applied via undo/redo — lets the capture
  // effect recognize its own change and skip recording it as a new revision.
  const appliedSignatureRef = useRef<string | null>(null);
  // Try-on items taken off by undo, kept so redo can put them back.
  const setAsidePreviewsRef = useRef(new Map<number, PreviewItem>());

  // ── Undo/redo: auto-capture a revision after every editor action ──
  // Every action already updates the ['houseFurniture', mapObjectId] cache
  // optimistically, so watching its signature captures all of them without
  // instrumenting individual call sites.
  // Try-on items are part of the layout history too (see history.ts).
  const previewMapObjectId = useFurniturePreviewStore((s) => s.previewMapObjectId);
  const previewItems = useFurniturePreviewStore((s) => s.previewItems);
  const previews = useMemo(
    () => roomPreviews({ previewMapObjectId, previewItems }, mapObjectId),
    [previewMapObjectId, previewItems, mapObjectId],
  );
  const currentSnapshotSignature = useMemo(
    () => snapshotSignature(toSnapshot(houseFurniture, previews)),
    [houseFurniture, previews]
  );

  useEffect(() => {
    if (!canManageFurniture || !houseFurnitureLoaded || !mapObjectId) return;
    // A drag gesture is in flight (suspend transaction). Skip recording every
    // intermediate cache write — the pointer-up handler records exactly one
    // final revision when the gesture ends. Don't touch appliedSignatureRef here:
    // undo/redo replay isn't suspended, so its bookkeeping must stay intact.
    if (sceneRef.current.drag.captureSuspended) return;
    // Skip the change we caused ourselves by applying an undo/redo target.
    if (appliedSignatureRef.current === currentSnapshotSignature) {
      appliedSignatureRef.current = null;
      return;
    }
    // Don't snapshot mid-flight server data (stale cache shown before a refetch
    // settles) — that would seed a bogus baseline / extra revision. User actions
    // update the cache via setQueryData without setting isFetching, so they're
    // still captured.
    if (houseFurnitureFetching) return;
    const snap = toSnapshot(houseFurniture, previews);
    const state = useInteriorHistoryStore.getState();
    if (state.mapObjectId !== mapObjectId || state.revisions.length === 0) {
      historyInit(mapObjectId, snap);
    } else {
      historyRecord(snap);
    }
    // Trigger on layout signature only — omit houseFurniture identity (would thrash).
    // historyInit/historyRecord/sceneRef: zustand actions + ref (stable / live-read).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- signature gate; not furniture ref thrash
  }, [currentSnapshotSignature, canManageFurniture, houseFurnitureLoaded, houseFurnitureFetching, mapObjectId, historyInit, historyRecord]);

  // Clear history when leaving the room (session-scoped undo).
  // historyReset — zustand action (stable); re-bind only when room id changes.
  useEffect(() => {
    return () => {
      historyReset();
    };
  }, [mapObjectId, historyReset]);

  // Replay a target revision onto the live room: one local layout write for every
  // item that differs (so the whole revision lands as one step), synced to the
  // backend as one atomic batch by layoutSync.
  const applyTargetSnapshot = useCallback(
    (target: InteriorSnapshot) => {
      const targetById = new Map(target.map((t) => [t.id, t]));
      const current = (queryClient.getQueryData<HouseFurniture[]>([
        'houseFurniture',
        mapObjectId,
      ]) ?? []);

      // Try-on items: put back their position / flip / floor-vs-surface; one tried on
      // after the target revision is taken off (kept aside so redo can put it back).
      const previewStore = useFurniturePreviewStore.getState();
      const livePreviews = roomPreviews(previewStore, mapObjectId);
      const resultingPreviews: PreviewItem[] = [];
      for (const p of livePreviews) {
        const tgt = targetById.get(previewSnapshotId(p.instanceId));
        if (!tgt) {
          setAsidePreviewsRef.current.set(p.instanceId, p);
          previewStore.removePreviewItem(p.instanceId);
          continue;
        }
        if (tgt.x === null || tgt.y === null) { resultingPreviews.push(p); continue; }
        if (tgt.x !== p.x || tgt.y !== p.y) previewStore.updatePreviewPosition(p.instanceId, tgt.x, tgt.y);
        if (tgt.placement_mode !== toPlacementMode(p.placement_mode)) previewStore.updatePreviewPlacementMode(p.instanceId, tgt.placement_mode);
        if (tgt.is_mirrored !== p.isMirrored) previewStore.togglePreviewMirror(p.instanceId);
        resultingPreviews.push({ ...p, x: tgt.x, y: tgt.y, isMirrored: tgt.is_mirrored, placement_mode: tgt.placement_mode });
      }
      const liveIds = new Set(livePreviews.map((p) => p.instanceId));
      for (const t of target) {
        if (!isPreviewSnapshotId(t.id)) continue;
        const instanceId = -t.id;
        const aside = setAsidePreviewsRef.current.get(instanceId);
        if (liveIds.has(instanceId) || !aside || t.x === null || t.y === null) continue;
        const restored = { ...aside, x: t.x, y: t.y, isMirrored: t.is_mirrored, placement_mode: t.placement_mode };
        previewStore.restorePreviewItem(mapObjectId, restored);
        setAsidePreviewsRef.current.delete(instanceId);
        resultingPreviews.push(restored);
      }

      const patches: LayoutPatch[] = [];
      for (const cur of current) {
        const tgt = targetById.get(cur.id);
        if (!tgt) {
          // Bought after the target revision → back to storage.
          if (cur.is_placed) patches.push({ id: cur.id, is_placed: false });
          continue;
        }
        const changed =
          cur.is_placed !== tgt.is_placed ||
          cur.x !== tgt.x ||
          cur.y !== tgt.y ||
          cur.is_mirrored !== tgt.is_mirrored ||
          toPlacementMode(cur.placement_mode) !== tgt.placement_mode ||
          (cur.rug_z_order ?? 0) !== tgt.rug_z_order ||
          (cur.wall_floor_offset ?? 0) !== tgt.wall_floor_offset;
        if (!changed) continue;
        if (!tgt.is_placed) {
          patches.push({ id: cur.id, is_placed: false });
        } else if (tgt.x !== null && tgt.y !== null) {
          patches.push({
            id: cur.id,
            is_placed: true,
            room_index: tgt.room_index,
            x: tgt.x,
            y: tgt.y,
            is_mirrored: tgt.is_mirrored,
            placement_mode: tgt.placement_mode,
            rug_z_order: tgt.rug_z_order,
            wall_floor_offset: tgt.wall_floor_offset,
          });
        }
      }

      // Mark the resulting layout as self-applied so the capture effect ignores it.
      const byId = new Map(patches.map((p) => [p.id, p]));
      const resulting = current.map((item) => {
        const p = byId.get(item.id);
        if (!p) return item;
        return p.is_placed === false ? { ...item, ...p, x: null, y: null } : { ...item, ...p };
      });
      appliedSignatureRef.current = snapshotSignature(toSnapshot(resulting as HouseFurniture[], resultingPreviews));
      stageLayout(queryClient, mapObjectId, patches);
      removeGreenOutline();
    },
    // removeGreenOutline may be unstable from parent; read latest via intentional omit
    // so undo/redo handlers keep a stable identity for keyboard bindings.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit removeGreenOutline identity thrash
    [mapObjectId, queryClient]
  );

  // historyUndo/historyRedo — zustand actions (stable identity for the store lifetime).
  const handleUndo = useCallback(() => {
    const snap = historyUndo();
    if (snap) applyTargetSnapshot(snap);
  }, [applyTargetSnapshot, historyUndo]);

  const handleRedo = useCallback(() => {
    const snap = historyRedo();
    if (snap) applyTargetSnapshot(snap);
  }, [applyTargetSnapshot, historyRedo]);

  return { canUndo, canRedo, handleUndo, handleRedo };
}
